import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  QrCode, 
  Settings, 
  Menu,
  X,
  LogOut,
  Crown,
  FileText,
  CreditCard,
  User,
  Mail,
  Server, Globe } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ApiService } from '../utils/api';
import { Button } from './ui/button';
import EventNav, { useEventPages } from './EventNav';
import { Card } from './ui/card';

const readPortalRef = () => {
  try {
    return localStorage.getItem('teamPortalEventId');
  } catch (_) {
    return null;
  }
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const isDemoMode = new URLSearchParams(location.search).get('demo') === 'true';

  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;

  // Team members see the event's sign-in branding (logo, title, colour) on every page of that event.
  const eventMatch = matchPath('/admin/events/:eventId/*', location.pathname);
  const brandEventRef = eventMatch?.params?.eventId || readPortalRef();
  const { data: brand } = useQuery({
    queryKey: ['teamPortal', brandEventRef],
    queryFn: () => ApiService.getTeamPortal(brandEventRef),
    enabled: !isAdminUser && !!brandEventRef,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const eventPages = useEventPages(eventMatch?.params?.eventId, user?.roles);
  const teamSidebar = !isAdminUser && !!eventMatch && eventPages.length > 1;
  const hasSidebar = isAdminUser || teamSidebar;
  const brandColor = brand?.portal?.primaryColor || '#2563eb';
  const brandLogo = brand?.portal?.logoUrl || brand?.eventLogo;
  const brandTitle = brand?.portal?.title || brand?.eventName;

  const { data: lanStatus } = useQuery({
    queryKey: ['lanMode'],
    queryFn: () => ApiService.getLanStatus(),
    enabled: isAdminUser,
    staleTime: Infinity,
    retry: false,
  });

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Events', href: '/admin/events', icon: Calendar },
    { name: 'Payment Merchants', href: '/admin/organizers', icon: CreditCard },
    { name: 'QR Generator', href: '/admin/qr-generator', icon: QrCode },
    { name: 'Website', href: '/admin/website', icon: Globe },
    ...(lanStatus?.lanMode ? [{ name: 'LAN Server', href: '/admin/lan', icon: Server }] : []),
    { 
      name: 'Settings', 
      href: '/admin/settings/forms', 
      icon: Settings,
      children: [
        { name: 'Form Builder', href: '/admin/settings/forms', icon: FileText },
        { name: 'Email Templates', href: '/admin/settings/email-templates', icon: Mail },
        { name: 'Coupons', href: '/admin/settings/coupons', icon: CreditCard },
      ]
    },
  ];

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {hasSidebar && sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {teamSidebar && (
        <div
          className={`fixed inset-y-0 left-0 z-50 w-60 transform border-r bg-white shadow-lg transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 lg:shadow-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
          style={brand ? { borderTop: `4px solid ${brandColor}` } : undefined}
        >
          <div className="flex h-16 items-center justify-between border-b px-6">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Event pages</span>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden" aria-label="Close menu">
              <X className="h-6 w-6" />
            </button>
          </div>
          <EventNav pages={eventPages} onNavigate={() => setSidebarOpen(false)} />
        </div>
      )}

      {isAdminUser && (
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="flex items-center justify-between h-16 px-6 border-b">
            <h1 className="text-xl font-bold text-gray-900">EventEase</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="mt-6 px-3">
            <div className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const hasChildren = item.children && item.children.length > 0;
                const isSettingsActive = item.name === 'Settings' && 
                  (location.pathname.startsWith('/admin/settings/forms') || 
                   location.pathname.startsWith('/admin/settings/email-templates') ||
                   location.pathname.startsWith('/admin/settings/coupons'));
                
                return (
                  <div key={item.name}>
                    <Link
                      to={item.href}
                      className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                        ${isActive(item.href) || isSettingsActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                    
                    {item.name === 'Events' && eventMatch && (
                      <EventNav pages={eventPages} variant="sidebar" onNavigate={() => setSidebarOpen(false)} />
                    )}
                    {/* Render children if this is the Settings item and it's active */}
                    {hasChildren && isSettingsActive && (
                      <div className="ml-6 mt-1 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`
                                group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                                ${isActive(child.href)
                                  ? 'bg-blue-50 text-blue-600'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                }
                              `}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <ChildIcon className="mr-3 h-4 w-4" />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          <div className="absolute bottom-0 w-full p-4 border-t">
            {isDemoMode ? (
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-2">Demo Mode</div>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/auth')}
                >
                  <User className="mr-2 h-4 w-4" />
                  Sign In
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/admin/account"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-2 rounded-md px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
                  title="Account & security"
                >
                  <User className="h-3.5 w-3.5" />
                  <span className="truncate">{user?.name || user?.email}</span>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={logout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header
          className="bg-white shadow-sm border-b"
          style={!isAdminUser && brand ? { borderTop: `4px solid ${brandColor}` } : undefined}
        >
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-3">
              {hasSidebar && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              )}
              
              {!isAdminUser && brand && (
                <Link to={eventPages[0]?.to || `/admin/events/${brand.slug || brand.eventId}/registrations`} className="flex min-w-0 items-center gap-3">
                  {brandLogo && (
                    <img src={brandLogo} alt="" className="h-9 w-auto max-w-[120px] object-contain" />
                  )}
                  <span className="truncate text-base font-semibold text-gray-900 sm:text-lg">{brandTitle}</span>
                </Link>
              )}
              {isDemoMode && (
                <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                  Demo Mode
                </div>
              )}
            </div>

            {/* For non-admin users, provide a logout button in the top bar */}
            {!isDemoMode && !isAdminUser && (
              <div className="flex shrink-0 items-center gap-2">
                <span className="hidden text-sm text-gray-500 md:inline">{user?.name || user?.email}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {!isAdminUser && brand?.portal?.bannerUrl && (
            <div
              className="relative h-24 overflow-hidden sm:h-32 lg:h-40"
              style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor}cc)` }}
            >
              <img
                src={brand.portal.bannerUrl}
                alt=""
                className={`absolute inset-0 h-full w-full ${brand.portal.bannerFit === 'CONTAIN' ? 'object-contain p-2' : 'object-cover'}`}
              />
              {brand.portal.subtitle && (
                <>
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
                  <p className="absolute bottom-2 left-4 right-4 truncate text-sm font-medium text-white sm:left-6 lg:left-8">{brand.portal.subtitle}</p>
                </>
              )}
            </div>
          )}
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
